import { IconAlertTriangle } from '@bbr/icons/alert-triangle-outline';
import { IconCircleCheck } from '@bbr/icons/circle-check-outline';
import { IconInfoCircle } from '@bbr/icons/info-circle-outline';
import { IconMoodSadDizzy } from '@bbr/icons/mood-sad-dizzy-outline';
import { Dynamic } from 'solid-js/web';
import { tv } from 'tailwind-variants';
import { Button } from '../forms/button.tsx';
import { Heading } from '../text/heading.tsx';
import type { IconComponent } from '@bbr/icons';
import type { ComponentProps, ParentProps } from 'solid-js';
import type { VariantProps } from 'tailwind-variants';

type Props = VariantProps<typeof classes> &
	ParentProps<{
		id?: string;
		icon?: IconComponent;
	}>;
export function Alert(props: Props) {
	return (
		<div class="@container w-full">
			<div
				role={props.variant && props.variant !== 'info' && props.variant !== 'default' ? 'alert' : 'status'}
				class={classes(props)}
				id={props.id}
			>
				<Dynamic
					data-alert="icon"
					component={props.icon ?? icons[props.variant ?? 'default']}
					class="col-start-1 row-start-1 size-6 shrink-0 self-start"
				/>
				{props.children}
			</div>
		</div>
	);
}

const icons: Record<NonNullable<VariantProps<typeof classes>['variant']>, IconComponent> = {
	default: IconInfoCircle,
	info: IconInfoCircle,
	success: IconCircleCheck,
	warning: IconAlertTriangle,
	danger: IconMoodSadDizzy,
};

const classes = tv({
	base: 'relative grid grid-cols-[min-content_1fr] items-center gap-x-2 rounded-lg border p-2 transition-colors @xl:grid-cols-[min-content_1fr_min-content] @xl:p-4 has-data-[alert=title]:**:@xl:data-[alert=action]:row-span-2 has-data-[alert=title]:**:@xl:data-[alert=action]:row-start-1',
	variants: {
		variant: {
			default: 'border-zinc-300 bg-zinc-200 dark:border-zinc-700 dark:bg-zinc-800',
			info: 'border-teal-200 bg-teal-50 text-black **:data-[alert=icon]:text-teal-700 dark:bg-teal-100',
			success: 'border-emerald-200 bg-emerald-50 text-black **:data-[alert=icon]:text-emerald-700 dark:bg-emerald-100',
			warning: 'border-orange-300 bg-orange-50 text-black **:data-[alert=icon]:text-orange-500 dark:bg-orange-200',
			danger: 'border-rose-300 bg-rose-50 text-black **:data-[alert=icon]:text-rose-500 dark:bg-rose-100',
		},
	},
	defaultVariants: {
		variant: 'info',
	},
});

Alert.Title = function AlertTitle(
	props: Omit<ComponentProps<typeof Heading>, 'level' | 'size'> &
		Partial<Pick<ComponentProps<typeof Heading>, 'level' | 'size'>>,
) {
	return <Heading data-alert="title" level="h2" size="h6" {...props} class="col-start-2 row-start-1" />;
};

Alert.Content = function AlertContent(props: ParentProps) {
	return (
		<div data-alert="content" class="col-start-2 text-sm">
			{props.children}
		</div>
	);
};

Alert.Action = function AlertAction(props: ComponentProps<typeof Button>) {
	return (
		<div data-alert="action" class="col-start-2 -ms-2 @xl:col-start-3 @xl:ms-0">
			<Button appearance="subtle" size="sm" parentAsHitTarget {...props} />
		</div>
	);
};
